/**
 * PsychoReportView — single-report web view with PDF download.
 *
 * URL: /dashboard/psycho-reports/:reportId
 *
 * Shows everything the PDF shows + a "Download PDF" button.  Linked
 * from:
 *   - the post-quiz redirect
 *   - the Founder Timeline psychometric phase card
 *   - the founders list (admin)
 *   - the founder's own profile page
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Compass, Download, ArrowLeft, AlertTriangle, CheckCircle2, Clock, ChevronRight,
  TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import OceanRadar from '@/components/OceanRadar';

const BIG5_LABELS = {
  openness: 'Openness',
  conscientiousness: 'Conscientiousness',
  extraversion: 'Extraversion',
  agreeableness: 'Agreeableness',
  neuroticism: 'Neuroticism',
};

function Bar({ label, value, max = 100 }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="grid grid-cols-12 gap-3 items-center text-sm">
      <div className="col-span-4 font-bold text-slate-700">{label}</div>
      <div className="col-span-7 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-[#8B3FC5]" style={{ width: `${pct}%`, transition: 'width 0.8s' }}/>
      </div>
      <div className="col-span-1 text-right font-bold text-[#5E1F8B]">{Math.round(value || 0)}</div>
    </div>
  );
}

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
}

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
export default function PsychoReportView() {
  const { reportId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cohort, setCohort] = useState(null);  // {overall.big_five_avg, per_industry[]}
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/startups/psycho/report/${reportId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load report.');
    } finally { setLoading(false); }
    // Cohort is best-effort — admin endpoint, founders get 403 silently.
    try {
      const { data: c } = await api.get('/psycho-cohort/');
      setCohort(c);
    } catch (err) {
      // Founder doesn't have cohort access — expected on first-time view.
      if (err?.response?.status && err.response.status !== 403) {
        console.error('PsychoReportView: cohort fetch failed', err);
      }
    }
  };
  useEffect(() => { load(); }, [reportId]);

  const downloadPdf = async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/startups/psycho/report/${reportId}.pdf`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Psychometric_${reportId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded.');
    } catch (e) {
      toast.error('Could not download PDF.');
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading report…</div>;
  if (!data) return null;

  const r = data.report;
  const isAdmin = user?.role === 'system_admin' || user?.role === 'holding_admin';

  return (
    <div data-testid="psycho-report-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <Link
            to={isAdmin && data.founder
              ? `/dashboard/founders/${data.founder.user_id}`
              : '/dashboard/founder'}
            className="ve-btn-ghost text-xs"
            data-testid="psycho-report-back"
          >
            <ArrowLeft size={12}/> Back to founder timeline
          </Link>
          <div className="ve-eyebrow mt-3">Psychometric Report</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            {data.founder?.name || 'Founder'}<br/>
            {data.startup?.name && (
              <span className="ve-gold-text text-2xl md:text-3xl">{data.startup.name}</span>
            )}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            {data.founder?.email}
            {data.startup?.industry && ` · ${data.startup.industry}`}
          </p>
        </div>
        <button
          onClick={downloadPdf}
          data-testid="psycho-report-download"
          className="ve-btn-primary self-start"
        >
          <Download size={14}/> Download PDF
        </button>
      </div>

      {/* Metadata strip */}
      <div className="mt-6 ve-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Report ID</div>
          <div className="font-mono mt-1">{r.report_id}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Completed</div>
          <div className="mt-1">{fmt(r.completed_at || r.created_at)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Duration</div>
          <div className="mt-1">
            {r.duration_seconds
              ? `${Math.floor(r.duration_seconds / 60)}m ${r.duration_seconds % 60}s`
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Answered</div>
          <div className="mt-1">
            <span className="text-emerald-700 font-bold">{r.answered_count} answered</span>
            {' · '}
            <span className="text-rose-700 font-bold">{r.skipped_count} skipped</span>
          </div>
        </div>
      </div>

      {/* DNA radar — founder vs industry vs cohort */}
      <FounderDNASection report={r} cohort={cohort} startup={data.startup}/>

      {/* Big 5 + composite */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="ve-card p-5">
          <h2 className="display text-lg font-bold">Big-Five OCEAN profile</h2>
          <div className="mt-4 space-y-3">
            {Object.entries(BIG5_LABELS).map(([k, label]) => (
              <Bar key={k} label={label} value={r.big_five?.[k] || 0}/>
            ))}
          </div>
        </div>
        <div className="ve-card p-5">
          <h2 className="display text-lg font-bold">Composite signals</h2>
          <div className="mt-4 space-y-3">
            <Bar label="Leadership index" value={r.leadership_index || 0}/>
            <Bar label="Risk appetite" value={r.risk_appetite || 0}/>
          </div>
          {r.summary && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">AI summary</div>
              <p className="text-sm text-slate-700 mt-2 italic leading-relaxed">{r.summary}</p>
            </div>
          )}
          {r.flags?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-rose-700 font-bold">Flags</div>
              <ul className="mt-2 space-y-1">
                {r.flags.map((f, i) => (
                  <li key={`f-${i}-${String(f).slice(0, 24)}`} className="text-xs text-rose-700 flex gap-1.5">
                    <AlertTriangle size={11} className="mt-0.5 shrink-0"/> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Transcript */}
      {r.questions?.length > 0 && (
        <div className="mt-6 ve-card p-5">
          <h2 className="display text-lg font-bold">Question-by-question transcript</h2>
          <div className="mt-4 space-y-2">
            {r.questions.map((q, i) => (
              <div
                key={`q-${i}-${q.dimension || ''}`}
                data-testid={`psycho-report-q-${i}`}
                className={`flex items-start gap-3 p-3 rounded border ${
                  q.skipped ? 'bg-rose-50/50 border-rose-200' : 'bg-slate-50/50 border-slate-200'
                }`}
              >
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 font-mono shrink-0 w-10">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800">{q.text}</p>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    Dimension · {q.dimension}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  {q.skipped ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-700">
                      Not answered
                    </span>
                  ) : (
                    <span className="text-sm font-bold text-[#5E1F8B]">{q.answer} / 5</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 text-[10px] uppercase tracking-[0.18em] text-slate-400 text-center">
        Source-of-truth: <code className="font-mono">psycho_reports.{r.report_id}</code> ·
        Generated by Claude Sonnet 4.5
      </div>
    </div>
  );
}


// ───────────── FounderDNASection — radar + delta cards ─────────────

function pickIndustryAvg(cohort, industry) {
  if (!cohort || !industry) return null;
  // Case-insensitive match to handle "FinTech" / "fintech" duplicates.
  const lc = String(industry).toLowerCase();
  const hit = (cohort.per_industry || []).find(
    (i) => String(i.industry || '').toLowerCase() === lc,
  );
  return hit ? hit.big_five_avg : null;
}

function deltaLabel(d) {
  if (d > 8) return { tone: 'emerald', Icon: TrendingUp, txt: `+${d.toFixed(0)}` };
  if (d < -8) return { tone: 'rose', Icon: TrendingDown, txt: `${d.toFixed(0)}` };
  return { tone: 'slate', Icon: Minus, txt: d.toFixed(0) };
}

function FounderDNASection({ report, cohort, startup }) {
  // Build the three series in the exact key order the radar expects.
  const founder = report.big_five || {};
  const industryAvg = pickIndustryAvg(cohort, startup?.industry);
  const cohortAvg = cohort?.overall?.big_five_avg || null;
  const industryLabel = startup?.industry ? `${startup.industry} avg` : 'Industry avg';

  // Build the delta-vs-cohort cards (only when cohort is available).
  const deltas = cohortAvg
    ? Object.entries(BIG5_LABELS).map(([k, label]) => {
        const f = Number(founder[k] || 0);
        const c = Number(cohortAvg[k] || 0);
        const delta = f - c;
        return { key: k, label, founder: f, cohort: c, delta };
      })
    : [];

  // Headline insight — pick the dimension with the LARGEST positive delta
  // and the LARGEST negative delta to call out.
  const sorted = [...deltas].sort((a, b) => b.delta - a.delta);
  const top = sorted.length ? sorted[0] : null;
  const bottom = sorted.length ? sorted[sorted.length - 1] : null;

  return (
    <div className="mt-6 ve-card p-6" data-testid="founder-dna-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="ve-eyebrow">Founder DNA</div>
          <h2 className="display text-xl font-bold mt-1">
            How you compare on five dimensions.
          </h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Gold = you. Rose dashed = {industryLabel}. Slate dotted = everyone we&apos;ve assessed. Hover a point for the exact value.
          </p>
        </div>
        {top && bottom && top.key !== bottom.key && (
          <div className="text-xs flex gap-3 flex-wrap" data-testid="founder-dna-headline">
            <span className="ve-card px-3 py-1.5 border-emerald-200 bg-emerald-50">
              <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-emerald-700">Strongest gap</span>
              <div className="font-bold text-emerald-800">
                {top.label} <span className="font-mono">+{(top.delta).toFixed(0)}</span>
              </div>
            </span>
            <span className="ve-card px-3 py-1.5 border-rose-200 bg-rose-50">
              <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-rose-700">Largest deficit</span>
              <div className="font-bold text-rose-800">
                {bottom.label} <span className="font-mono">{(bottom.delta).toFixed(0)}</span>
              </div>
            </span>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8 items-start">
        <div className="flex justify-center">
          <OceanRadar
            founder={founder}
            industryAvg={industryAvg}
            industryLabel={industryLabel}
            cohortAvg={cohortAvg}
            size={400}
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">
            {cohortAvg ? 'Your delta vs cohort average' : 'Cohort comparison unavailable for this view.'}
          </div>
          {!cohortAvg && (
            <p className="text-xs text-slate-500 italic">
              Cohort and industry overlays are an admin-only data product. Ask your reviewer to share the cohort dashboard.
            </p>
          )}
          {cohortAvg && (
            <div className="space-y-2">
              {deltas.map(({ key, label, founder: f, cohort: c, delta }) => {
                const meta = deltaLabel(delta);
                const Icon = meta.Icon;
                return (
                  <div
                    key={key}
                    data-testid={`dna-delta-${key}`}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded border border-slate-200 bg-white/50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-bold text-sm text-slate-800 truncate">{label}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs shrink-0">
                      <div className="text-right">
                        <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400">You</div>
                        <div className="font-bold tabular-nums text-[#5E1F8B]">{Math.round(f)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400">Cohort</div>
                        <div className="font-bold tabular-nums text-slate-600">{Math.round(c)}</div>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded font-bold tabular-nums
                        ${meta.tone === 'emerald' ? 'bg-emerald-50 text-emerald-700' :
                          meta.tone === 'rose' ? 'bg-rose-50 text-rose-700' :
                          'bg-slate-100 text-slate-600'}`}>
                        <Icon size={11}/> {meta.txt}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
